import {zodResolver} from '@hookform/resolvers/zod';
import {isFrozenTaskDate} from '@nursio-link/common/utils';
import {Repeat2} from 'lucide-react';
import {useEffect} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {useConfirm} from '@/components/confirm-dialog/useConfirm';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form as FormProvider} from '@/components/ui/form';
import {FormSkeleton} from '@/components/ui/skeleton';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import type {Task} from '@/services/graphqlHooks';
import {
  useCompleteTaskMutation,
  useDeleteTaskMutation,
  useFindRecurringTaskByIdQuery,
  useGetTaskQuery,
  useRejectTaskMutation,
  useUpdateTaskMutation,
} from '@/services/graphqlHooks';

import type {
  TaskActionFormValues,
  TaskFormValues,
  TaskFromDefaultValueInput,
} from '../../utils/tasksFormInputs';
import {
  mapTaskFormValuesToTaskInput,
  StandardTaskVariant,
  taskActionFormDefaultValues,
  taskActionFormSchema,
  taskFormDefaultValues,
  taskFormSchema,
} from '../../utils/tasksFormInputs';
import {isStandardTask} from '../../utils/tasksHelpers';
import ArchivedTaskNotice from '../ArchivedTaskNotice';
import EditTaskCommentFormFields from '../task-form-fields/EditTaskCommentFormFields';
import TaskActionFormFields from '../task-form-fields/TaskActionFormFields';
import TaskFormFields from '../TaskFormFields';
import TaskFormPreview from '../TaskFormPreview';

export type EditTaskDialogProps = DialogProps;

const EditTaskDialog = ({onOpenChange, ...props}: EditTaskDialogProps) => {
  const {t} = useTranslation();
  const {confirm} = useConfirm();
  const {toast} = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('task') || '';

  const deleteTask = useDeleteTaskMutation(graphqlClient);
  const updateTask = useUpdateTaskMutation(graphqlClient);
  const rejectTask = useRejectTaskMutation(graphqlClient);
  const completeTask = useCompleteTaskMutation(graphqlClient);

  const isDisabled =
    deleteTask.isPending ||
    updateTask.isPending ||
    rejectTask.isPending ||
    completeTask.isPending;

  const {data: taskData, isLoading: isTaskLoading} = useGetTaskQuery(
    graphqlClient,
    {id},
    {
      enabled: !!(id && props.open),
      staleTime: 0,
      refetchOnMount: 'always',
      initialData: undefined,
    }
  );

  const task = taskData?.getTask as Task;

  const {data: parentRecurringTask} = useFindRecurringTaskByIdQuery(
    graphqlClient,
    {id: task?.parentRecurringTaskId || ''},
    {
      staleTime: 0,
      enabled: !!task?.parentRecurringTaskId,
      refetchOnMount: 'always',
      initialData: undefined,
    }
  );

  const recurringTaskExist = !!parentRecurringTask?.findRecurringTaskById;

  const taskForm = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      ...taskFormDefaultValues(task as TaskFromDefaultValueInput),
      standardTaskVariant: StandardTaskVariant.OneTime,
    },
  });

  const actionForm = useForm<TaskActionFormValues>({
    resolver: zodResolver(taskActionFormSchema),
    defaultValues: taskActionFormDefaultValues,
  });

  const actionMode = useWatch({control: actionForm.control, name: 'mode'});

  useEffect(() => {
    if (!task) return;
    taskForm.reset({
      ...taskFormDefaultValues(task as TaskFromDefaultValueInput),
      standardTaskVariant: StandardTaskVariant.OneTime,
    });
    taskForm.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  useEffect(() => {
    if (!props.open || !task) return;

    actionForm.reset({
      ...taskActionFormDefaultValues,
      mode:
        task.completedBy && !isFrozenTaskDate(task.date)
          ? 'update-comment'
          : 'none',
      comment: task.comment ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, task]);

  const handleUpdateTask = async (values: TaskFormValues) => {
    try {
      await updateTask.mutateAsync({
        input: {id, ...mapTaskFormValuesToTaskInput(values)},
      });
      toast({
        title: t('Task updated'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleTaskAction = async (values: TaskActionFormValues) => {
    try {
      if (values.mode === 'reject') {
        await rejectTask.mutateAsync({
          input: {id, rejectionNote: values.rejectionNote || ''},
        });
        toast({title: t('Task rejected'), variant: 'success'});
        onOpenChange?.(true);
        return;
      }

      if (values.mode === 'comment') {
        await completeTask.mutateAsync({input: {id, comment: values.comment}});
        toast({title: t('Task completed'), variant: 'success'});
        onOpenChange?.(true);
        return;
      }

      if (values.mode !== 'update-comment') return;

      await updateTask.mutateAsync({
        input: {
          id,
          title: task.title,
          date: task.date!,
          content: task.content ?? undefined,
          completeUntil: task.completeUntil ?? undefined,
          completedBy: task.completedBy ?? undefined,
          completedAt: task.completedAt ?? undefined,
          assignedToEmployee: task.assignedToEmployee ?? undefined,
          assignedToResident: task.assignedToResident ?? undefined,
          comment: values.comment || null,
        },
      });
      toast({title: t('Task updated'), variant: 'success'});
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await confirm({
        title: t('Delete task'),
        message: t('Are you sure you want to delete this task?'),
      });
      await deleteTask.mutateAsync({id});
      toast({
        title: t('Task deleted'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const isArchived = isFrozenTaskDate(task?.date);
  const isReadOnly = !!task?.completedBy || isArchived;
  const canEditComment = !!task?.completedBy && !isArchived;
  const showTaskActions =
    isStandardTask(task) && !task?.completedBy && !isArchived;
  const canEditTask = !!task && !isReadOnly;
  const showDeleteButton = canEditTask && actionMode === 'none';
  const showSubmitButton = canEditTask || canEditComment;

  const getSubmitButtonText = () => {
    if (canEditComment) return t('Save');
    if (actionMode === 'comment') return t('Complete');
    if (actionMode === 'reject') return t('Reject task');

    return t('Submit');
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) actionForm.reset(taskActionFormDefaultValues);
    onOpenChange?.(open);
  };

  return (
    <Dialog {...props} onOpenChange={handleOpenChange}>
      <DialogContent asChild>
        <form
          onSubmit={
            actionMode !== 'none'
              ? actionForm.handleSubmit(handleTaskAction)
              : taskForm.handleSubmit(handleUpdateTask)
          }
          noValidate
        >
          <DialogHeader className="md:hidden">
            <DialogTitle>{t('Task')}</DialogTitle>
          </DialogHeader>
          <DialogSection className="py-0">
            {isTaskLoading && <FormSkeleton />}
            {!isTaskLoading && !task && <div>{t('Task not found')}</div>}

            {task && !isReadOnly && (
              <FormProvider {...taskForm}>
                <TaskFormFields form={taskForm} hideTaskType />
              </FormProvider>
            )}
            {task && isReadOnly && <TaskFormPreview task={task} />}
            {task && canEditComment && (
              <FormProvider {...actionForm}>
                <EditTaskCommentFormFields form={actionForm} />
              </FormProvider>
            )}
            {task && isArchived && <ArchivedTaskNotice />}

            {showTaskActions && (
              <FormProvider {...actionForm}>
                <div className="mt-10">
                  <TaskActionFormFields form={actionForm} />
                </div>
              </FormProvider>
            )}

            {task?.parentRecurringTaskId && (
              <div className="m-2">
                <Button
                  type="button"
                  disabled={!recurringTaskExist}
                  className="flex flex-row gap-2"
                  variant="link"
                  size="text"
                  onClick={() => {
                    searchParams.append(
                      'recurringTask',
                      task.parentRecurringTaskId!
                    );
                    setSearchParams(searchParams);
                  }}
                >
                  <Repeat2 size="16" />{' '}
                  {recurringTaskExist
                    ? t('Edit recurring task')
                    : t('Recurring task deleted')}
                </Button>
              </div>
            )}
          </DialogSection>
          <DialogFooter>
            {showDeleteButton && (
              <Button
                type="button"
                className="sm:mr-auto"
                variant="warning"
                onClick={() => handleDelete()}
                disabled={isDisabled}
              >
                {t('Delete task')}
              </Button>
            )}
            <Button
              variant={showSubmitButton ? 'secondary' : 'primary'}
              type="button"
              onClick={() => handleOpenChange(false)}
            >
              {t('Close')}
            </Button>
            {showSubmitButton && (
              <Button variant="primary" type="submit" disabled={isDisabled}>
                {getSubmitButtonText()}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTaskDialog;
